import { useEffect, useCallback } from 'react';

/**
 * Hook personalizado para manejar la navegación por teclado en listas de sugerencias
 * 
 * @param {Array} listaSugerencias - Array de objetos con sugerencias de búsqueda
 * @param {number} selectedIndex - Índice del elemento actualmente seleccionado
 * @param {Function} setSelectedIndex - Función para actualizar el índice seleccionado
 * @param {Function} onSelect - Callback ejecutado al seleccionar una sugerencia (Enter)
 * @param {Function} onClose - Callback ejecutado al cerrar las sugerencias (Escape)
 * @param {boolean} isActive - Indica si la navegación por teclado está activa
 */
export const useKeyboardNavigation = (
  listaSugerencias,
  selectedIndex,
  setSelectedIndex,
  onSelect,
  onClose,
  isActive
) => {
  /**
   * Maneja los eventos de teclado para la navegación
   * - ArrowDown: Navega hacia abajo en la lista
   * - ArrowUp: Navega hacia arriba en la lista
   * - Enter: Selecciona la sugerencia actual
   * - Escape: Cierra la lista de sugerencias
   */
  const handleKeyDown = useCallback((e) => {
    if (!isActive || !listaSugerencias?.length) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, listaSugerencias.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && listaSugerencias[selectedIndex]) {
          const suggestion = listaSugerencias[selectedIndex];
          const sugerenciaText = suggestion.sugerencia || suggestion;
          onSelect(sugerenciaText);
        }
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
    }
  }, [listaSugerencias, selectedIndex, setSelectedIndex, onSelect, onClose, isActive]);

  useEffect(() => {
    if (!isActive) return;
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, isActive]);
};